import { Box, IconButton, Typography, CircularProgress, Fade, Container } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { WidgetContext } from "src/contexts/WidgetContext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import ThumbsResponse from "../ThumbsResponse/ThumbsResponse";
import { useAuth0 } from "@auth0/auth0-react";
import ChartScatter from "../Charts/ChartScatter";
import ChartBar from "../Charts/ChartBar";
import ChartLine from "../Charts/ChartLine";
import SourceStories from "../SourceStories/SourceStories";
import { useTypewriter } from "src/utils/useTypewriter";
import ThoughtItem from "../ThoughtItem/ToughtItem";
import ChartStackBar from "../Charts/ChartStackBar";
import { isPlatform } from "@ionic/react";
import { useHandleShare } from "src/utils/handleShare";
import UpgradeSubscriptionFree from "../PaymentMethod/UpgradeSubscriptionFree";
import UpgradeSubscriptionNonLogged from "../PaymentMethod/UpgradeSubscriptionNonLogged";
import SourceIcon from "../SourceStories/SourceIcon";

const ChartRenderer = ({ chartData }) => {
  if (!chartData) return null;
  switch (chartData.chart_type) {
    case "scatter":
      return <ChartScatter chartData={chartData} />;
    case "bar":
      return <ChartBar chartData={chartData} />;
    case "stacked_bar":
      return <ChartStackBar chartData={chartData} />;
    case "line":
      return <ChartLine chartData={chartData} />;
    default:
      console.warn("Unknown chart type:", chartData.chart_type);
      return null;
  }
};

const StartResponseStream = ({
  onClose,
  startData,
  loadingStart,
  setShouldPlayAudio,
  streamStatus,
  streamThought,
  isGeneratingResult,
  hasStartAnimationCompleted,
  setHasStartAnimationCompleted,
  streamProgress,
  setStreamProgress,
  isWaitingForChart,
  isRecoveringFromBackground,
  selectedButton,
  setIsPaymentOpen,
  setInitialPaymentView,
  isExpanded,
  onToggleExpand,
}) => {
  const { selectedPartner, selectedTeamInfo, NON_LOGGED_IN_CONFIG, isLoggedInAuth0ExperienceGlobal, aiSettings } = useContext(WidgetContext);
  const { user } = useAuth0();
  const authId = user?.sub || NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
  const containerRef = useRef(null);
  const iosOrAndroid = isPlatform("hybrid");
  const { handleShare, isCheckmark } = useHandleShare();

  // Only show UpgradeSubscriptionFree if this response was generated with FREE model
  const wasFreeModel = startData?.model === "RotoGPT FREE";

  //start_analysis is the text that is displayed in the start response stream
  const analysisText = startData?.start_analysis || "";
  // chartsData is now an array of chart objects
  const chartsData = startData?.chart_data || [];
  // typewriterSpeed is 0 for ios and android, 1 for desktop
  const typewriterSpeed = iosOrAndroid ? 0 : 1;
  // Call the hook with the saved progress as the initial value.
  const typewriterEffectText = useTypewriter(analysisText, typewriterSpeed, streamProgress.start);

  // Decide which text to display based on whether the animation has already completed.
  const displayedAnalysis = hasStartAnimationCompleted ? analysisText : typewriterEffectText;

  const displayedAnalysisRef = useRef("");
  displayedAnalysisRef.current = displayedAnalysis;

  useEffect(() => {
    // This cleanup function runs when the component unmounts (is closed).
    // It saves the current displayed text to the parent component.
    return () => {
      setStreamProgress((prev) => ({ ...prev, start: displayedAnalysisRef.current }));
    };
  }, [setStreamProgress]);

  // This regex splits the text by any chart placeholder, e.g., [chart_0]
  // The result is an array of text segments that were between the placeholders.

  // isTypingFinished is a boolean that indicates if the typing is finished
  const isTypingFinished = !loadingStart && analysisText.length > 0 && displayedAnalysis.length === analysisText.length;

  useEffect(() => {
    // When the typewriter finishes for the first time, set the flag
    // so subsequent views of this data are instant.
    if (isTypingFinished) {
      setHasStartAnimationCompleted(true);
      // Optional: Clear the saved progress once fully rendered to free memory.
      // The hasStartAnimationCompleted flag will now ensure it's instant on subsequent views within the same session.
      setStreamProgress((prev) => ({ ...prev, start: "" }));
    }
    // console.log('startData stream', startData);
    // We only want this effect to run when isTypingFinished changes.
  }, [isTypingFinished, setHasStartAnimationCompleted, setStreamProgress]);

  // Check if the typewriter has finished rendering all text content received *so far*.
  const hasTypewriterFinishedCurrentText = displayedAnalysis.length === analysisText.length;

  // --- Logic to prevent partial table rendering ---
  let textForMarkdown = displayedAnalysis;
  let isStreamingTable = false;
  if (!isTypingFinished && !hasStartAnimationCompleted) {
    const lastBlockIndex = displayedAnalysis.lastIndexOf("\n\n");
    // Check if the last part of the text seems to be a table
    if (lastBlockIndex !== -1) {
      const lastBlock = displayedAnalysis.substring(lastBlockIndex + 2);
      if (lastBlock.includes("|")) {
        isStreamingTable = true;
        // Only show the text that came before the table
        textForMarkdown = displayedAnalysis.substring(0, lastBlockIndex);
      }
    } else if (displayedAnalysis.includes("|")) {
      // Handles case where the table is at the very beginning of the response
      isStreamingTable = true;
      textForMarkdown = "";
    }
  }
  // --- End of logic ---

  // This regex splits the text by any chart placeholder, e.g., [chart_0]
  // The result is an array of text segments that were between the placeholders.
  const textSegments = textForMarkdown.split(/\[CHART:.*?\]|\[chart_\d+\]/gi); // NEW: Split by the new placeholder format

  // NEW: Find all chart placeholders in the final analysis text to determine their order.
  const chartPlaceholders = analysisText.match(/\[CHART:.*?\]|\[chart_\d+\]/gi) || [];
  // const chartPlaceholders = analysisText.match(/\[CHART:.*?\]|\[chart_\d+\]/gi) || [];
  // Define the recovery condition
  const isRecovering = isRecoveringFromBackground && !startData?.start_analysis && !isGeneratingResult;

  // NEW: Determine if we should show the main analysis content. This is our main switch.
  const showAnalysis = isGeneratingResult || (startData?.start_analysis && startData.start_analysis.length > 0);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "black",
        overflow: "hidden",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header container */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "black",
        }}
      >
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={1}>
          <IconButton aria-label="back" onClick={onClose}>
            <ArrowBackIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
          </IconButton>
          <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.white[100]} textAlign="center" className={styles.tradeTeam}>
            Start for {selectedPartner}
          </Typography>
          <Box display="flex" alignItems="center">
            {onToggleExpand && aiSettings.quan && (
              <IconButton aria-label="toggle-expand" onClick={onToggleExpand}>
                {isExpanded ? (
                  <KeyboardArrowDownIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
                ) : (
                  <KeyboardArrowUpIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
                )}
              </IconButton>
            )}
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Content container */}
      <div
        ref={containerRef}
        className={styles.noScrollbar}
        style={{
          flex: 1,
          position: "relative",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          scrollBehavior: "smooth",
          backgroundColor: "black",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Fade in={true} timeout={500}>
          <Container
            maxWidth="lg"
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            {showAnalysis && !isRecovering ? (
              // C. Show the final result (analysis, charts, etc.)
              <>
                {/* Render the first segment of text */}
                <Typography variant="h4" fontSize={"16px"} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} p={1} pb={0}>
                  <ReactMarkdown className={styles.reactMarkdownStart} remarkPlugins={[remarkGfm]}>
                    {textSegments[0]}
                  </ReactMarkdown>
                </Typography>

                {/* Loop through ALL placeholders found in the text, not just charts in chartsData */}
                {/* This ensures text segments after placeholders are always rendered, even if chart_data wasn't received */}
                {chartPlaceholders.map((chartPlaceholder, index) => {
                  // Check if the typewriter has already passed this chart's placeholder
                  const placeholderPosition = analysisText.indexOf(chartPlaceholder);
                  const currentTypewriterPosition = displayedAnalysis.length;

                  // Only show content if the typewriter has passed this placeholder position
                  const shouldShowContent = placeholderPosition !== -1 && currentTypewriterPosition > placeholderPosition;

                  if (!shouldShowContent) {
                    return null;
                  }

                  // Check if we have actual chart data for this placeholder
                  const chart = chartsData[index];

                  // The text that comes after the chart (can be undefined and that's fine)
                  const nextTextSegment = textSegments[index + 1];

                  // Check if we should show loader after this chart
                  // Show loader if: there's no immediate next text segment AND we haven't finished typing AND we're waiting for a chart
                  const shouldShowLoaderAfterChart = !nextTextSegment && !isTypingFinished && isWaitingForChart && !chart;

                  return (
                    <Box key={`chart-block-${index}`}>
                      {/* Only render the chart if we have data for it */}
                      {chart && (
                        <Box key={`chart-render-${index}`} sx={{ py: 1, width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                          <ChartRenderer chartData={chart} />
                        </Box>
                      )}

                      {/* Show loader after placeholder if no immediate content and still streaming/waiting for chart */}
                      {shouldShowLoaderAfterChart && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          {/* <SuspenseLoader /> */}
                          {/* <CircularProgress size={24} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> */}
                          <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Thinking...</Typography>
                        </Box>
                      )}

                      {/* Always render the next text segment if it exists */}
                      {nextTextSegment && (
                        <Typography variant="h4" fontSize={"16px"} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} p={1} pb={0}>
                          <ReactMarkdown className={styles.reactMarkdownStart} remarkPlugins={[remarkGfm]}>
                            {nextTextSegment}
                          </ReactMarkdown>
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {isStreamingTable && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Generating table...</Typography>
                  </Box>
                )}

                {/* Show loader while waiting for a chart to be generated, but only after the preceding text has finished typing. */}
                {isWaitingForChart && hasTypewriterFinishedCurrentText && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    {/* <CircularProgress size={24} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> */}
                    <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Generating chart...</Typography>
                  </Box>
                )}
              </>
            ) : (
              // A/B. Show Status/Thoughts OR Recovery Message
              <>
                {isRecovering ? (
                  // B. Show the recovery message if necessary
                  <Box sx={{ py: 2, textAlign: "center" }}>
                    <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>Recovering your analysis, please wait</Typography>
                  </Box>
                ) : (
                  // A. show Status and Thought if we are not showing the final result
                  <>
                    {streamThought ? (
                      <ThoughtItem text={streamThought} />
                    ) : streamStatus === "Retrieving your start analysis..." ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={30} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
                      </Box>
                    ) : (
                      streamStatus && <Typography sx={{ color: "grey", fontStyle: "italic", mb: 2, textAlign: "center" }}>{streamStatus}</Typography>
                    )}
                  </>
                )}
              </>
            )}

            {/* Thumbs up/down feedback - show only when typing is finished AND we are not streaming a table */}
            {isTypingFinished && !isStreamingTable && (
              <Box sx={{ width: "100%", bgcolor: "#000000" }}>
                {/* Source icon */}
                <SourceIcon customPaddingBottom={0.5} customPaddingTop={0.5} />
                {/* Only show upgrade button if this response was generated with FREE model */}
                {wasFreeModel && (
                  isLoggedInAuth0ExperienceGlobal ? (
                    <UpgradeSubscriptionFree setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} />
                  ) : (
                    <UpgradeSubscriptionNonLogged setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} />
                  )
                )}
                <ThumbsResponse
                  messageId={startData?.request_id}
                  userId={authId}
                  message={"No message, its start section"}
                  response={startData?.start_analysis}
                  section="start"
                  platformId={selectedTeamInfo?.platformId}
                  leagueId={selectedTeamInfo?.leagueId}
                  teamName={selectedTeamInfo?.teamName}
                  handleShare={() => handleShare(startData?.request_id, "Check out this start/sit analysis!", startData?.audioUrl, startData?.share_url)}
                  isCheckmark={isCheckmark}
                  shareUrl={startData?.share_url}
                />
                {/* <Box sx={{ width: "100%", bgcolor: "#000000", mt: 0, mb: 0 }}>
                  <SourceStories customPaddingBottom={1} customPaddingTop={0.5} />
                </Box> */}

              </Box>
            )}
          </Container>
        </Fade>
      </div>

      {/* {isTypingFinished && (
        <Box sx={{ width: "100%", bgcolor: "#000000", mt: 0, mb: 0 }}>
          <SourceStories customPaddingBottom={1} customPaddingTop={1} />
        </Box> */}
      {/* )} */}
    </Box>
  );
};

export default StartResponseStream;
